import type {
  DragConstraint,
  DragUpdateResult,
  SerializableVector3,
  SnapAnchor,
} from "@project/emerge-types";
import { Html } from "@react-three/drei";
import type { ThreeEvent } from "@react-three/fiber";
import { useThree } from "@react-three/fiber";
import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  BufferGeometry,
  type Euler,
  Float32BufferAttribute,
  type Group,
  Line3,
  LineBasicMaterial,
  Plane,
  Raycaster,
  Vector2,
  Vector3,
} from "three";
import { useDragStore } from "../stores/dragStore";
import { Arrow3D } from "./Arrow3D";

/** Distance threshold in world units for snapping to anchors */
const SNAP_THRESHOLD = 5;

/** Type for React Three Fiber components that accept userData */
type WithUserData = {
  userData?: { isDragHelper?: boolean };
};

/** Display values returned from drag update */
export type DragDisplayValues = DragUpdateResult<unknown>["displayValues"];

/** Props for the DraggablePart component */
export interface DraggablePartProps {
  /** Unique identifier for this part (used for drag state tracking) */
  partId: string;
  /** The part's current world position */
  position: Vector3;
  /** The part's rotation (Euler angles) */
  rotation?: Euler;
  /** The drag constraint defining movement limits */
  constraint: DragConstraint;
  /** Called continuously during drag with the constrained position. Returns display values to show. */
  onDrag?: (position: Vector3) => DragDisplayValues | undefined;
  /** Called when drag ends with the final position (may be snapped) */
  onDragEnd?: (position: Vector3, snappedAnchor?: SnapAnchor) => void;
  /** Called when drag starts - return cleanup function to re-enable controls */
  onDragStart?: () => (() => void) | undefined;
  /** Children to render (the actual part) */
  children: React.ReactNode;
  /** Whether dragging is enabled (default: true) */
  enabled?: boolean;
  /** Whether to show hover highlight (default: true) */
  showHoverHighlight?: boolean;
  /** Show debug visuals for constraint bounds (default: false) */
  debug?: boolean;
  /** If true, do not update local state during drag; rely on parent to update position prop (default: false) */
  controlled?: boolean;
}

/**
 * Converts a SerializableVector3 tuple to a Three.js Vector3.
 */
function toVector3(v: SerializableVector3): Vector3 {
  return new Vector3(v[0], v[1], v[2]);
}

/**
 * Projects a point onto an edge constraint (1D line segment).
 * The edge is defined by bounds.min to bounds.max.
 */
function projectOntoEdge(
  point: Vector3,
  bounds: { min: Vector3; max: Vector3 },
): Vector3 {
  // Create line segment from bounds
  const line = new Line3(bounds.min, bounds.max);
  const projected = new Vector3();
  // true = clamp to line segment (not infinite line)
  line.closestPointToPoint(point, true, projected);
  return projected;
}

/**
 * Projects a point onto a surface constraint (2D plane).
 * Uses the center of bounds as the plane point.
 */
function projectOntoSurface(
  point: Vector3,
  planeNormal: Vector3,
  bounds: { min: Vector3; max: Vector3 },
): Vector3 {
  // Use center of bounds as the plane point
  const planePoint = bounds.min.clone().add(bounds.max).multiplyScalar(0.5);

  // Create plane from normal and center point
  const plane = new Plane().setFromNormalAndCoplanarPoint(
    planeNormal,
    planePoint,
  );

  // Project the point onto the plane
  const projected = new Vector3();
  plane.projectPoint(point, projected);

  // Clamp to bounds
  projected.x = Math.max(bounds.min.x, Math.min(bounds.max.x, projected.x));
  projected.y = Math.max(bounds.min.y, Math.min(bounds.max.y, projected.y));
  projected.z = Math.max(bounds.min.z, Math.min(bounds.max.z, projected.z));

  return projected;
}

/**
 * Finds the closest snap anchor within the threshold distance.
 */
function findSnapAnchor(
  position: Vector3,
  anchors: SnapAnchor[],
  threshold: number,
): SnapAnchor | undefined {
  let closest: SnapAnchor | undefined;
  let closestDistance = threshold;

  for (const anchor of anchors) {
    const anchorPos = toVector3(anchor.position);
    const distance = position.distanceTo(anchorPos);
    if (distance < closestDistance) {
      closest = anchor;
      closestDistance = distance;
    }
  }

  return closest;
}

/**
 * Component that wraps a part and makes it draggable according to its constraint.
 *
 * Features:
 * - Projects pointer movement onto edge (1D) or surface (2D) constraints
 * - Respects movement bounds
 * - Snaps to anchor points when within threshold
 * - Shows visual guides during drag
 */
export const DraggablePart: React.FC<DraggablePartProps> = ({
  partId,
  position,
  rotation,
  constraint,
  onDrag,
  onDragEnd,
  onDragStart,
  children,
  enabled = true,
  showHoverHighlight = true,
  debug = false,
  controlled = false,
}) => {
  // Use global drag store to track which part is being dragged
  // Use selector to only re-render when this specific part's drag state changes
  const draggingPartId = useDragStore((state) => state.draggingPartId);
  const startDrag = useDragStore((state) => state.startDrag);
  const endDrag = useDragStore((state) => state.endDrag);
  const showArrows = draggingPartId === partId;
  const { camera, gl } = useThree();
  const groupRef = useRef<Group>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [dragPosition, setDragPosition] = useState<Vector3 | null>(null);
  const [hoveredAnchor, setHoveredAnchor] = useState<SnapAnchor | undefined>();
  const [displayValues, setDisplayValues] =
    useState<DragDisplayValues>(undefined);
  const isDraggingRef = useRef(false); // Ref for reliable access in event handlers
  const didDragRef = useRef(false); // Track if a drag occurred (to suppress click)
  const initialPositionRef = useRef<Vector3 | null>(null);
  const dragPlaneRef = useRef<Plane | null>(null);
  const offsetRef = useRef<Vector3 | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);
  const listenerCleanupRef = useRef<(() => void) | null>(null);
  const raycasterRef = useRef(new Raycaster());
  const hoveredAnchorRef = useRef<SnapAnchor | undefined>(undefined);

  // Convert constraint bounds to Vector3
  const bounds = useMemo(
    () => ({
      min: toVector3(constraint.bounds.min),
      max: toVector3(constraint.bounds.max),
    }),
    [constraint.bounds],
  );

  const planeNormal = useMemo(
    () =>
      constraint.planeNormal
        ? toVector3(constraint.planeNormal).normalize()
        : undefined,
    [constraint.planeNormal],
  );

  /**
   * Projects a world point onto the constraint.
   */
  const projectPoint = useCallback(
    (worldPoint: Vector3): Vector3 => {
      if (constraint.type === "edge") {
        return projectOntoEdge(worldPoint, bounds);
      } else if (constraint.type === "surface" && planeNormal) {
        return projectOntoSurface(worldPoint, planeNormal, bounds);
      }
      // Fallback: clamp to bounds
      return worldPoint.clone().clamp(bounds.min, bounds.max);
    },
    [constraint.type, planeNormal, bounds],
  );

  /**
   * Raycast from screen position onto a plane.
   */
  const raycastOntoPlane = useCallback(
    (clientX: number, clientY: number, plane: Plane): Vector3 | null => {
      const rect = gl.domElement.getBoundingClientRect();
      const ndc = new Vector2(
        ((clientX - rect.left) / rect.width) * 2 - 1,
        -((clientY - rect.top) / rect.height) * 2 + 1,
      );
      raycasterRef.current.setFromCamera(ndc, camera);
      const ray = raycasterRef.current.ray;
      const intersection = new Vector3();
      const result = ray.intersectPlane(plane, intersection);

      if (debug) {
        // ... debug logic if needed, but removing standard logs
      }
      return result;
    },
    [camera, gl.domElement, debug],
  );

  /**
   * Global pointer move handler for drag (attached to window during drag).
   * Uses refs instead of state to avoid stale closure issues.
   */
  const handleGlobalPointerMove = useCallback(
    (event: PointerEvent) => {
      if (
        !isDraggingRef.current ||
        !dragPlaneRef.current ||
        !offsetRef.current
      ) {
        return;
      }

      // Raycast onto the drag plane
      const planePoint = raycastOntoPlane(
        event.clientX,
        event.clientY,
        dragPlaneRef.current,
      );

      if (!planePoint) {
        return;
      }

      // Subtract offset to get the intended part center position (in World Space)
      const newWorldPosition = planePoint.clone().sub(offsetRef.current);

      // Convert to Local Space (relative to parent, matching 'position' prop space)
      const newLocalPosition = newWorldPosition.clone();
      if (groupRef.current?.parent) {
        groupRef.current.parent.worldToLocal(newLocalPosition);
      }

      // Project onto constraint
      const constrainedPosition = projectPoint(newLocalPosition);

      // Check for snap
      const snapAnchor = findSnapAnchor(
        constrainedPosition,
        constraint.snapAnchors,
        SNAP_THRESHOLD,
      );

      const finalPos = snapAnchor
        ? toVector3(snapAnchor.position)
        : constrainedPosition;

      if (!controlled) {
        if (snapAnchor) {
          setDragPosition(finalPos);
          setHoveredAnchor(snapAnchor);
        } else {
          setDragPosition(finalPos);
          setHoveredAnchor(undefined);
        }
      } else {
        // In controlled mode, we still update hovered anchor for visuals?
        // Or should visuals also be controlled?
        // For now let's keep local hover state for feedback even if position is controlled
        setHoveredAnchor(snapAnchor);
      }

      // Notify parent and capture display values
      const newDisplayValues = onDrag?.(finalPos);
      if (newDisplayValues) {
        setDisplayValues(newDisplayValues);
      }
    },
    [
      projectPoint,
      constraint.snapAnchors,
      onDrag,
      raycastOntoPlane,
      controlled,
    ],
  );

  // Keep hoveredAnchorRef in sync with state (for use in pointer up handler to avoid stale closure)
  useEffect(() => {
    hoveredAnchorRef.current = hoveredAnchor;
  }, [hoveredAnchor]);

  /**
   * Global pointer up handler for drag (attached to window during drag).
   * Uses refs instead of state to avoid stale closure issues.
   */
  const handleGlobalPointerUp = useCallback(
    (_event: PointerEvent) => {
      if (!isDraggingRef.current) {
        return;
      }

      isDraggingRef.current = false;

      // Remove event listeners FIRST to prevent any race conditions
      if (listenerCleanupRef.current) {
        listenerCleanupRef.current();
        listenerCleanupRef.current = null;
      }

      // Call cleanup to re-enable controls
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }

      setIsDragging(false);
      endDrag(); // Clear global drag state (with delay for click suppression)
      setDisplayValues(undefined); // Clear display values

      // Use ref for hoveredAnchor to avoid stale closure
      const currentHoveredAnchor = hoveredAnchorRef.current;
      setDragPosition((currentDragPos) => {
        // Use functional update to get current value
        if (currentDragPos) {
          onDragEnd?.(currentDragPos, currentHoveredAnchor);
        }
        return null;
      });
      setHoveredAnchor(undefined);
      initialPositionRef.current = null;
      dragPlaneRef.current = null;
      offsetRef.current = null;

      // Reset didDragRef after a short delay to allow click event to be suppressed
      setTimeout(() => {
        didDragRef.current = false;
      }, 100);
    },
    [onDragEnd, endDrag],
  );

  /**
   * Handle click - suppress if a drag just occurred.
   * This prevents the lid toggle from firing after a drag operation.
   */
  const handleClick = useCallback((event: ThreeEvent<MouseEvent>) => {
    if (didDragRef.current) {
      event.stopPropagation();
    }
  }, []);

  /**
   * Handle pointer down - start drag.
   * Requires Command key (⌘) on macOS or Ctrl key on Windows/Linux to be held
   * to prevent accidental drags while orbiting.
   */
  const handlePointerDown = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      if (!enabled) return;

      const isLeftClick = event.nativeEvent.button === 0;
      const hasModifierKey =
        event.nativeEvent.metaKey || event.nativeEvent.ctrlKey;
      if (!isLeftClick || !hasModifierKey) return;
      if (isDraggingRef.current) return;

      event.stopPropagation();

      initialPositionRef.current = position.clone();

      // Get true world position for plane calculation
      const worldPosition = new Vector3();
      if (groupRef.current) {
        groupRef.current.getWorldPosition(worldPosition);
      } else {
        worldPosition.copy(position);
      }

      // Determine drag plane normal
      // For surfaces, drag along the surface plane for 1:1 movement
      // For edges or fallback, face the camera to ensure intersection
      let dragPlaneNormal: Vector3;
      if (constraint.type === "surface" && planeNormal) {
        dragPlaneNormal = planeNormal;
      } else {
        dragPlaneNormal = camera.position
          .clone()
          .sub(worldPosition)
          .normalize();
      }

      dragPlaneRef.current = new Plane().setFromNormalAndCoplanarPoint(
        dragPlaneNormal,
        worldPosition,
      );

      // Calculate offset: where on the plane we clicked relative to the part center (in world space)
      const clickPoint = raycastOntoPlane(
        event.nativeEvent.clientX,
        event.nativeEvent.clientY,
        dragPlaneRef.current,
      );

      if (clickPoint) {
        offsetRef.current = clickPoint.clone().sub(worldPosition);
      } else {
        offsetRef.current = new Vector3();
      }

      isDraggingRef.current = true;
      didDragRef.current = true; // Mark that a drag started (to suppress click)
      setIsDragging(true);
      startDrag(partId); // Track which part is dragging in global store

      // Attach listeners immediately (don't wait for useEffect)
      const moveHandler = handleGlobalPointerMove;
      const upHandler = handleGlobalPointerUp;
      window.addEventListener("pointermove", moveHandler);
      window.addEventListener("pointerup", upHandler);
      gl.domElement.addEventListener("pointermove", moveHandler);
      gl.domElement.addEventListener("pointerup", upHandler);

      // Store cleanup for listeners
      listenerCleanupRef.current = () => {
        window.removeEventListener("pointermove", moveHandler);
        window.removeEventListener("pointerup", upHandler);
        gl.domElement.removeEventListener("pointermove", moveHandler);
        gl.domElement.removeEventListener("pointerup", upHandler);
      };

      // Call onDragStart and store cleanup function
      const cleanup = onDragStart?.();
      if (cleanup) {
        cleanupRef.current = cleanup;
      }
    },
    [
      enabled,
      position,
      onDragStart,
      camera,
      raycastOntoPlane,
      gl.domElement,
      handleGlobalPointerMove,
      handleGlobalPointerUp,
      constraint.type,
      planeNormal,
      partId,
      startDrag,
    ],
  );

  // Cleanup listeners on unmount ONLY (empty deps = only runs on unmount)
  useEffect(() => {
    return () => {
      // Clean up any active drag state on unmount
      if (isDraggingRef.current) {
        isDraggingRef.current = false;
        // Don't call endDrag here - it can cause re-render loops
      }
      if (listenerCleanupRef.current) {
        listenerCleanupRef.current();
        listenerCleanupRef.current = null;
      }
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - only cleanup on actual unmount

  /**
   * Handle pointer enter - show hover state.
   */
  const handlePointerEnter = useCallback(() => {
    if (!enabled || isDragging) return;
    setIsHovered(true);
  }, [enabled, isDragging]);

  /**
   * Handle pointer leave - hide hover state (but don't end drag).
   */
  const handlePointerLeave = useCallback(() => {
    if (!isDragging) {
      setIsHovered(false);
    }
  }, [isDragging]);

  // Show visuals when dragging, hovering, or in debug mode
  const showVisuals = isDragging || isHovered || debug;

  // Generate constraint visualization geometry
  const constraintLineGeometry = useMemo(() => {
    if (!showVisuals) return null;

    const geometry = new BufferGeometry();

    if (constraint.type === "edge") {
      // Line from min to max along axis
      const points = [
        bounds.min.x,
        bounds.min.y,
        bounds.min.z,
        bounds.max.x,
        bounds.max.y,
        bounds.max.z,
      ];
      geometry.setAttribute("position", new Float32BufferAttribute(points, 3));
    } else if (constraint.type === "surface" && planeNormal) {
      // Rectangle outline for surface - compute proper corners based on plane
      const { min, max } = bounds;

      // For surfaces, we need to construct a proper rectangle on the plane
      // Use the bounds to create 4 corners, keeping the normal coordinate fixed
      let points: number[];

      if (Math.abs(planeNormal.x) > 0.9) {
        // YZ plane
        const x = (min.x + max.x) / 2;
        points = [
          x,
          min.y,
          min.z,
          x,
          max.y,
          min.z,
          x,
          max.y,
          min.z,
          x,
          max.y,
          max.z,
          x,
          max.y,
          max.z,
          x,
          min.y,
          max.z,
          x,
          min.y,
          max.z,
          x,
          min.y,
          min.z,
        ];
      } else if (Math.abs(planeNormal.y) > 0.9) {
        // XZ plane
        const y = (min.y + max.y) / 2;
        points = [
          min.x,
          y,
          min.z,
          max.x,
          y,
          min.z,
          max.x,
          y,
          min.z,
          max.x,
          y,
          max.z,
          max.x,
          y,
          max.z,
          min.x,
          y,
          max.z,
          min.x,
          y,
          max.z,
          min.x,
          y,
          min.z,
        ];
      } else {
        // XY plane (normal is Z)
        const z = (min.z + max.z) / 2;
        points = [
          min.x,
          min.y,
          z,
          max.x,
          min.y,
          z,
          max.x,
          min.y,
          z,
          max.x,
          max.y,
          z,
          max.x,
          max.y,
          z,
          min.x,
          max.y,
          z,
          min.x,
          max.y,
          z,
          min.x,
          min.y,
          z,
        ];
      }
      geometry.setAttribute("position", new Float32BufferAttribute(points, 3));
    }

    return geometry;
  }, [showVisuals, constraint.type, bounds, planeNormal]);

  const constraintLineMaterial = useMemo(() => {
    let color: number;
    let opacity: number;

    if (isDragging) {
      color = 0x00ff00;
      opacity = 1;
    } else if (debug) {
      color = 0xff00ff;
      opacity = 0.8;
    } else {
      color = 0x88ccff;
      opacity = 0.4;
    }

    return new LineBasicMaterial({
      color,
      opacity,
      transparent: true,
      linewidth: 2,
      depthTest: false,
    });
  }, [isDragging, debug]);

  // Calculate arrow directions based on constraint type
  const arrowDirections = useMemo(() => {
    const directions: Vector3[] = [];

    if (constraint.type === "edge" && constraint.axis) {
      // Edge constraint: show arrows along the axis (both directions)
      const axis = new Vector3(
        constraint.axis[0],
        constraint.axis[1],
        constraint.axis[2],
      ).normalize();
      directions.push(axis.clone());
      directions.push(axis.clone().negate());
    } else if (constraint.type === "surface" && planeNormal) {
      // Surface constraint: show arrows aligned with world axes that lie in the plane
      const normal = planeNormal.clone().normalize();
      const worldAxes = [
        new Vector3(1, 0, 0), // X
        new Vector3(0, 1, 0), // Y
        new Vector3(0, 0, 1), // Z
      ];

      for (const axis of worldAxes) {
        // Check if axis is parallel to normal (dot product close to ±1)
        const dot = Math.abs(normal.dot(axis));
        if (dot < 0.9) {
          // Project axis onto plane: axis - (axis · normal) * normal
          const projected = axis
            .clone()
            .sub(normal.clone().multiplyScalar(axis.dot(normal)))
            .normalize();
          directions.push(projected);
          directions.push(projected.clone().negate());
        }
      }
    }

    return directions;
  }, [constraint.type, constraint.axis, planeNormal]);

  // Compute current display position (drag position or original)
  const displayPosition = dragPosition || position;

  return (
    <group>
      {/* The draggable part */}
      {/* biome-ignore lint/a11y/noStaticElementInteractions: Three.js group element, not DOM */}
      <group
        ref={groupRef}
        position={[displayPosition.x, displayPosition.y, displayPosition.z]}
        rotation={rotation ? [rotation.x, rotation.y, rotation.z] : undefined}
        onPointerDown={enabled ? handlePointerDown : undefined}
        onPointerEnter={
          enabled && showHoverHighlight ? handlePointerEnter : undefined
        }
        onPointerLeave={enabled ? handlePointerLeave : undefined}
        onClick={handleClick}
      >
        {children}
      </group>

      {/* Direction arrows - rendered at part position but WITHOUT rotation (world-aligned) */}
      {showArrows && (
        <group
          position={[displayPosition.x, displayPosition.y, displayPosition.z]}
          {...({ userData: { isDragHelper: true } } as WithUserData)}
        >
          {arrowDirections.map((direction) => {
            // Create a stable key from direction components
            const dirKey = `${direction.x.toFixed(2)}_${direction.y.toFixed(2)}_${direction.z.toFixed(2)}`;
            return <Arrow3D key={`arrow-${dirKey}`} direction={direction} />;
          })}
        </group>
      )}

      {/* Display values during drag */}
      {displayValues && (
        <Html
          position={[
            displayPosition.x,
            displayPosition.y + 80,
            displayPosition.z,
          ]}
          center
          style={{
            pointerEvents: "none",
            userSelect: "none",
          }}
        >
          <div
            style={{
              background: "rgba(0, 0, 0, 0.8)",
              color: "#fff",
              padding: "4px 8px",
              borderRadius: "4px",
              fontSize: "12px",
              fontFamily: "monospace",
              whiteSpace: "nowrap",
              display: "flex",
              gap: "8px",
            }}
          >
            {displayValues.primary && (
              <span>
                {displayValues.primary.label}: {displayValues.primary.value}
                {displayValues.primary.unit}
              </span>
            )}
            {displayValues.secondary && (
              <span>
                {displayValues.secondary.label}: {displayValues.secondary.value}
                {displayValues.secondary.unit}
              </span>
            )}
          </div>
        </Html>
      )}

      {/* Constraint visualization (on hover, during drag, or debug) */}
      {showVisuals && constraintLineGeometry && (
        <lineSegments
          geometry={constraintLineGeometry}
          material={constraintLineMaterial}
          renderOrder={999}
          {...({ userData: { isDragHelper: true } } as WithUserData)}
        />
      )}

      {/* Debug: Show bounds corners */}
      {debug && (
        <group {...({ userData: { isDragHelper: true } } as WithUserData)}>
          {/* Min corner */}
          <mesh
            position={[bounds.min.x, bounds.min.y, bounds.min.z]}
            renderOrder={999}
          >
            <boxGeometry args={[5, 5, 5]} />
            <meshBasicMaterial
              color={0xff0000}
              opacity={0.8}
              transparent
              depthTest={false}
            />
          </mesh>
          {/* Max corner */}
          <mesh
            position={[bounds.max.x, bounds.max.y, bounds.max.z]}
            renderOrder={999}
          >
            <boxGeometry args={[5, 5, 5]} />
            <meshBasicMaterial
              color={0x00ff00}
              opacity={0.8}
              transparent
              depthTest={false}
            />
          </mesh>
          {/* Current position marker */}
          <mesh
            position={[position.x, position.y, position.z]}
            renderOrder={999}
          >
            <sphereGeometry args={[8, 16, 16]} />
            <meshBasicMaterial
              color={0x0000ff}
              opacity={0.5}
              transparent
              depthTest={false}
            />
          </mesh>
        </group>
      )}

      {/* Snap anchor points (during drag or debug) */}
      {(isDragging || debug) &&
        constraint.snapAnchors.map((anchor) => {
          const anchorPos = toVector3(anchor.position);
          const anchorIsHovered = hoveredAnchor === anchor;
          return (
            <mesh
              key={`anchor-${anchor.position.join(",")}`}
              position={[anchorPos.x, anchorPos.y, anchorPos.z]}
              renderOrder={999}
              {...({ userData: { isDragHelper: true } } as WithUserData)}
            >
              <sphereGeometry args={[anchorIsHovered ? 8 : 5, 16, 16]} />
              <meshBasicMaterial
                color={anchorIsHovered ? 0x00ff00 : 0xffff00}
                opacity={anchorIsHovered ? 1 : 0.8}
                transparent
                depthTest={false}
              />
            </mesh>
          );
        })}

      {/* Debug: Show drag position if dragging */}
      {debug && dragPosition && (
        <mesh
          position={[dragPosition.x, dragPosition.y, dragPosition.z]}
          renderOrder={999}
          {...({ userData: { isDragHelper: true } } as WithUserData)}
        >
          <sphereGeometry args={[10, 16, 16]} />
          <meshBasicMaterial
            color={0x00ffff}
            opacity={0.8}
            transparent
            depthTest={false}
          />
        </mesh>
      )}

      {/* Hover indicator - subtle glow around draggable part */}
      {isHovered && !isDragging && showHoverHighlight && (
        <mesh
          position={[displayPosition.x, displayPosition.y, displayPosition.z]}
          {...({ userData: { isDragHelper: true } } as WithUserData)}
        >
          <sphereGeometry args={[8, 8, 8]} />
          <meshBasicMaterial color={0x4488ff} opacity={0.15} transparent />
        </mesh>
      )}
    </group>
  );
};

export default DraggablePart;
